/**
 * WebSocket Service - Phase 3: Single Multiplexed Connection
 *
 * Architecture:
 * - ONE WebSocket connection per user (not per chat)
 * - Connects to /ws/user/ on app startup
 * - Backend automatically sends messages for ALL user's chats
 * - Messages are routed by chat_id in the message payload
 * - Integrates with tokenRefreshService for automatic token refresh
 */

import { IChannel, WEBSOCKET_MESSAGE_TYPES } from "../types/chat";
import { chatService } from "./chatService";
import type { ModelOptions } from "./apiService";
import { networkEventManager } from "./networkEventManager";
import { redirectToLogin } from "../utils/backendUrl";
import { tokenRefreshService } from "./tokenRefreshService";
import { wsManager } from "./websocketManager";
import { WS_EVENTS } from "../types/wsEvents";
import {
  closedAtFromWsMessage,
  isChatClosedWsMessage,
} from "../utils/chatClosed";

type EventCallback = (data: any) => void;

/**
 * How long sendAndAwaitAck waits for the backend to confirm a write.
 *
 * Long enough to absorb a slow round trip, short enough that a user who clicked
 * Download is not left staring at a disabled button — past this we tell them the
 * save failed rather than hand them a stale document.
 */
const ACK_TIMEOUT_MS = 5000;

/** Global refs so all module instances and callers share a single connection (avoids duplicate WS in same tab). */
const GLOBAL_WS_KEY = "__app_ws";
function getGlobalWsRef(): {
  socket: WebSocket | null;
  connectionPromise: Promise<boolean> | null;
  service: WebSocketService | null;
} {
  if (typeof window === "undefined")
    return { socket: null, connectionPromise: null, service: null };
  const w = window as any;
  if (!w[GLOBAL_WS_KEY])
    w[GLOBAL_WS_KEY] = { socket: null, connectionPromise: null, service: null };
  return w[GLOBAL_WS_KEY];
}

/**
 * Shape a chat (as any of our APIs return it) into the sidebar's IChannel.
 * Shared by every path that adds a chat to the sidebar without a refetch —
 * local creation, cloning, and chats announced over the socket.
 */
function toChannel(chat: any, extra?: Partial<IChannel>): IChannel {
  return {
    ...chat,
    id: chat.id,
    name: chat.id,
    description: chat.title || "Chat",
    pinned: chat.is_pinned || chat.isPinned || false,
    label: chat.title || "",
    metadata: chat.metadata || {},
    tags: chat.tags || [],
    claim_number: chat.claim_number,
    ...extra,
  };
}

class WebSocketService {
  // Connection state
  private ws: WebSocket | null = null;
  private isConnected: boolean = false;
  private isIntentionallyClosing: boolean = false;
  private connectingPromise: Promise<boolean> | null = null;

  // Active chat tracking (for UI - e.g., notification filtering)
  private activeChat: string | null = null;

  // Event management
  private listeners: Record<string, EventCallback[]> = {};

  // local_id -> resolver, for senders that must not continue until their write
  // has landed. Filled by sendAndAwaitAck, drained by handleMessage.
  private pendingAcks: Map<string, () => void> = new Map();

  // Last config flags snapshot received from the backend (readable by late subscribers)
  public configFlags: Record<string, unknown> = {};

  // Connection monitoring (backend sends heartbeats, we just track last received)
  private lastHeartbeat: number = Date.now();
  private reconnectAttempts: number = 0;
  private reconnectDelay: number = 1000;
  private maxReconnectDelay: number = 30000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isReconnecting: boolean = false;

  // Network state
  private isOnline: boolean = navigator.onLine;

  // One controller behind every global listener, so teardown is a single
  // abort. Deliberately not tied to `disconnect()`: that is transient here — a
  // tenant switch and App's effect cleanup both disconnect and immediately
  // reconnect — and these must outlive it, `online` being what drives recovery
  // while the socket is down. An AbortController is one-shot, so aborting there
  // would leave a reconnected service with no listeners at all.
  private listenerAbort = new AbortController();

  constructor() {
    const { signal } = this.listenerAbort;
    // Listen for online/offline events
    window.addEventListener("online", this.handleOnline.bind(this), { signal });
    window.addEventListener("offline", this.handleOffline.bind(this), {
      signal,
    });
    // A frozen or suspended tab dispatches no onmessage, so it acks nothing and
    // reads offline with the tab still open. Acking the moment it comes back
    // cuts the recovery from a full heartbeat interval to about a second.
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.visibilityState === "visible") {
          this.sendHeartbeatAck();
        }
      },
      { signal },
    );
  }

  /**
   * Drop this instance's global listeners.
   *
   * A page holds one service for its whole life, so production never needs
   * this. Tests build a fresh module per case, and without it every dead
   * instance stays subscribed to the same jsdom `document` for the rest of the
   * file — with only their `isConnected` early return keeping them from acting
   * on a shared event.
   */
  public dispose() {
    this.listenerAbort.abort();
  }

  // ========== Public API ==========

  /**
   * Connect to user-level WebSocket
   * Called once on app startup/login
   *
   * Ensures a single connection: reuses OPEN socket or pending promise; closes any stale socket before creating a new one.
   */
  public async connectUser(): Promise<boolean> {
    const global = getGlobalWsRef();

    // Already connected (use global ref so all callers / module instances share one socket)
    const existingSocket = global.socket ?? this.ws;
    if (existingSocket && existingSocket.readyState === WebSocket.OPEN) {
      this.ws = existingSocket;
      this.isConnected = true;
      global.socket = existingSocket;
      return true;
    }

    // Connection in progress - return existing promise (single connection guarantee)
    const existingPromise = global.connectionPromise ?? this.connectingPromise;
    if (existingPromise) {
      return existingPromise;
    }

    // Ensure no stale socket: close and clear if not OPEN
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    if (global.socket) {
      global.socket.close();
      global.socket = null;
    }

    // Start new connection
    const websocketUrl = this.getUserWebSocketUrl();

    // Create and store connection promise immediately (global + instance) so all callers share it
    const connectionPromise = new Promise<boolean>((resolve) => {
      let settled = false;
      const settle = (value: boolean) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };

      try {
        this.ws = new WebSocket(websocketUrl);
        global.socket = this.ws;

        this.ws.onopen = () => {
          this.isConnected = true;
          this.isIntentionallyClosing = false;
          this.reconnectAttempts = 0;
          this.lastHeartbeat = Date.now();

          const g = getGlobalWsRef();
          this.connectingPromise = null;
          g.connectionPromise = null;
          g.socket = this.ws;

          tokenRefreshService.setOnTokenRefreshedCallback(() => {
            this.notifyTokenRefreshed();
          });
          tokenRefreshService.reset();

          // Notify manager
          wsManager.onUserConnectionOpened(this.ws!);

          // Emit connect event
          this.emit("connect", { type: "user" });

          settle(true);
        };

        this.ws.onmessage = (event: MessageEvent) => {
          this.handleMessage(event);
        };

        this.ws.onerror = (error) => {
          console.error("[WS] Connection error:", error);
          const g = getGlobalWsRef();
          this.connectingPromise = null;
          g.connectionPromise = null;
          g.socket = null;
          this.emit("error", { type: "connection_error", error });
        };

        this.ws.onclose = (event: CloseEvent) => {
          const g = getGlobalWsRef();
          this.connectingPromise = null;
          g.connectionPromise = null;
          g.socket = null;
          settle(false);
          this.handleDisconnect(event);
        };

        // Timeout after 10 seconds
        setTimeout(() => {
          if (!settled) {
            console.error("[WS] Connection timeout");
            const g = getGlobalWsRef();
            this.connectingPromise = null;
            g.connectionPromise = null;
            g.socket = null;
            settle(false);
          }
        }, 10000);
      } catch (error) {
        console.error("[WS] Failed to create WebSocket:", error);
        const g = getGlobalWsRef();
        this.connectingPromise = null;
        g.connectionPromise = null;
        g.socket = null;
        settle(false);
      }
    });

    this.connectingPromise = connectionPromise;
    global.connectionPromise = connectionPromise;
    return connectionPromise;
  }

  /**
   * Set the active chat (currently visible in UI)
   * Used for UI purposes like filtering notifications
   *
   * @param chatId - The chat ID to set as active, or null to clear
   */
  public setActiveChat(chatId: string | null) {
    this.activeChat = chatId;
    wsManager.setActiveChat(chatId);
  }

  /**
   * Send message to a specific chat.
   * If not connected (e.g. after page refresh on /channels/[id]), waits for connection first.
   * @returns local_id that can be used to track the message
   */
  public async sendMessage(
    chatId: string,
    content: string,
    files?: any[],
    letter_content?: string,
    agent_name?: string,
    localId?: string,
    metadata?: Record<string, any>,
    { model_version }: ModelOptions = {},
  ): Promise<string> {
    // Generate local ID if not provided
    const local_id =
      localId ||
      `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Ensure connected (e.g. after page refresh, connection may still be establishing)
    if (!this.isConnected || !this.ws) {
      const connected = await this.connectUser();
      if (!connected || !this.ws) {
        console.error("[WS] Not connected, cannot send message", {
          isConnected: this.isConnected,
          wsExists: !!this.ws,
          wsReadyState: this.ws?.readyState,
        });
        this.emit("error", { type: "not_connected", chatId });
        return local_id;
      }
    }

    if (this.ws!.readyState !== WebSocket.OPEN) {
      console.error("[WS] WebSocket not in OPEN state", {
        readyState: this.ws!.readyState,
        chatId,
      });
      this.emit("error", {
        type: "websocket_not_open",
        chatId,
        readyState: this.ws!.readyState,
      });
      return local_id;
    }

    // Prepare message payload
    const payload: any = {
      type: "message",
      chat_id: chatId,
      content: content || "",
      local_id: local_id, // Include local_id so backend can echo it back
    };

    if (files?.length) {
      payload.files = files
        .map((f) => f.url || f.file_url)
        .filter((url) => url);
    }

    if (letter_content) {
      payload.letter_content = letter_content;
    }

    if (agent_name) {
      payload.agent_name = agent_name;
    }

    if (metadata) {
      payload.metadata = metadata;
    }

    // null is meaningful (backend default model); only undefined is omitted.
    if (model_version !== undefined) {
      payload.model_version = model_version;
    }

    try {
      // Send message
      this.ws!.send(JSON.stringify(payload));
    } catch (error) {
      console.error("[WS] Error sending message:", error);
      this.emit("error", { type: "send_error", chatId, error });
      return local_id;
    }

    // Set as active chat when sending
    this.setActiveChat(chatId);

    // Emit event for UI
    this.emit("message:sent", { chatId, message: content, files, local_id });

    return local_id;
  }

  /**
   * Ask the backend to re-dispatch a saved message that never reached the
   * agent (REV-2553). Returns false when the frame could not be sent.
   */
  public async sendRetry(chatId: string, messageId: string): Promise<boolean> {
    if (!this.isConnected || !this.ws) {
      const connected = await this.connectUser();
      if (!connected || !this.ws) {
        this.emit("error", { type: "not_connected", chatId });
        return false;
      }
    }

    if (this.ws!.readyState !== WebSocket.OPEN) {
      this.emit("error", {
        type: "websocket_not_open",
        chatId,
        readyState: this.ws!.readyState,
      });
      return false;
    }

    try {
      this.ws!.send(
        JSON.stringify({
          type: "retry_message",
          chat_id: chatId,
          message_id: messageId,
        }),
      );
    } catch (error) {
      console.error("[WS] Error sending retry:", error);
      this.emit("error", { type: "send_error", chatId, error });
      return false;
    }

    this.setActiveChat(chatId);
    return true;
  }

  /**
   * Send a message and resolve only once the backend has written it.
   *
   * sendMessage resolves as soon as the frame is on the socket, which is not the
   * same as the row existing — anything that then reads the chat over HTTP can
   * race the save. The consumer acks *after* committing (api/user_consumer.py
   * sends `message_ack` after save_message_with_files returns), so the ack is the
   * point from which a subsequent read is guaranteed to see this content.
   *
   * Rejects when no ack arrives inside `timeoutMs`. Callers are expected to treat
   * that as "the save did not happen" rather than carry on regardless.
   */
  public async sendAndAwaitAck(
    chatId: string,
    content: string,
    letterContent?: string,
    timeoutMs: number = ACK_TIMEOUT_MS,
  ): Promise<void> {
    const localId = `sync-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

    // Registered before sending: on a fast round trip the ack can arrive while
    // sendMessage is still awaiting a reconnect.
    const acked = this.waitForAck(localId, timeoutMs);
    // sendMessage reports send failures by emitting and returning, which would
    // leave `acked` to reject with nothing attached. Attaching a handler keeps
    // that from surfacing as an unhandled rejection; the await below still sees
    // the rejection.
    acked.catch(() => {});

    await this.sendMessage(
      chatId,
      content,
      undefined,
      letterContent,
      undefined,
      localId,
    );

    await acked;
  }

  private waitForAck(localId: string, timeoutMs: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingAcks.delete(localId);
        reject(new Error(`No acknowledgement for message ${localId}`));
      }, timeoutMs);

      this.pendingAcks.set(localId, () => {
        clearTimeout(timer);
        this.pendingAcks.delete(localId);
        resolve();
      });
    });
  }

  /**
   * Create new chat and set it as active
   */
  public async createChatAndConnect(
    title?: string,
    initialMessage?: string,
    tag?: string,
    files?: any[],
    letter_content?: string,
    agent_name?: string,
    claimId?: string,
    metadata?: Record<string, any>,
    experiments?: Record<string, boolean>,
    initialAssistantMessage?: string,
    localId?: string,
    { model_version }: ModelOptions = {},
  ) {
    try {
      // Ensure we're connected first
      if (!this.isConnected) {
        const connected = await this.connectUser();
        if (!connected) {
          throw new Error("Failed to establish WebSocket connection");
        }
      }

      // Create chat via API
      const chatResponse = await chatService.createChat({
        title: claimId ? `Claim #${claimId}` : "", // Set claim number as title, or let backend generate
        initialMessage,
        tag,
        metadata: { source: "frontend" },
        claimId,
        experiments,
        initialAssistantMessage,
        model_version,
      });

      if (!chatResponse.success || !chatResponse.data) {
        throw new Error(chatResponse.error || "Failed to create chat");
      }

      const chat = chatResponse.data;

      // Emit new channel for sidebar
      this.emit(
        WS_EVENTS.CHANNEL_ADD,
        toChannel(chat, {
          agent_name,
          claim_number: claimId,
          files_count: chat.files_count ?? (files?.length || 0),
        }),
      );

      // Set as active chat
      this.setActiveChat(chat.id);

      // Send initial message if provided
      if (initialMessage || (files && files.length > 0)) {
        // Carry the caller's optimistic local id (REV-2353): the backend echoes
        // it back on `message_ack`, which is the only thing that swaps the
        // local id on the store's optimistic row for the server one. Passing
        // `undefined` here minted an unrelated id, so the first message of a
        // new chat kept a `local-` id for the rest of the session — and the
        // REV-2348 owner watchdog's poll, deduping by server id, could not
        // recognise it and appended the server's copy as a second message.
        this.sendMessage(
          chat.id,
          initialMessage || "",
          files,
          letter_content,
          agent_name,
          localId,
          metadata,
          { model_version },
        );
      }

      return chat;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      console.error("[WS] Failed to create chat:", errorMessage);
      this.emit("error", { type: "chat_creation_failed", error: errorMessage });
      throw error;
    }
  }

  /**
   * Clone an existing chat (REV-1679) and add the new chat to the sidebar.
   *
   * Mirrors createChatAndConnect's sidebar wiring: emits CHANNEL_ADD so the
   * clone is prepended client-side (deduped by id). We cannot rely on
   * refreshChannels() here — it short-circuits when the active filter is
   * unchanged, so it would silently skip the fetch and the clone would never
   * appear. Returns the new chat; throws on failure for the caller to surface.
   */
  public async cloneChat(sourceChatId: string) {
    const response = await chatService.cloneChat(sourceChatId);
    if (!response.success || !response.data) {
      throw new Error(response.error || "Failed to clone chat");
    }

    const chat = response.data;
    this.emit(WS_EVENTS.CHANNEL_ADD, toChannel(chat));

    return chat;
  }

  /**
   * Disconnect WebSocket
   * Called on logout or app unmount
   * Note: Token refresh service is managed by AuthContext, not here
   */
  public disconnect() {
    this.isIntentionallyClosing = true;
    this.isReconnecting = false;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const global = getGlobalWsRef();
    this.connectingPromise = null;
    global.connectionPromise = null;

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    if (global.socket) {
      global.socket.close();
      global.socket = null;
    }

    this.isConnected = false;
    this.activeChat = null;
  }

  /**
   * List all channels by fetching all pages
   * Backend uses large page size (500), but we still fetch all pages to ensure we get everything
   */
  public async listChannels(page: number = 1): Promise<{
    channels: IChannel[];
    hasNextPage: boolean;
    totalCount: number;
  }> {
    try {
      const allChannels: IChannel[] = [];
      let currentPage = page;
      let hasMorePages = true;
      const maxPages = 10; // Safety limit to prevent infinite loops

      // Fetch all pages until we get all chats
      while (hasMorePages && currentPage <= maxPages) {
        const response = await chatService.getChats({
          page: currentPage,
          sortBy: "updatedAt",
          sortOrder: "desc",
        });

        if (response.success && response.data) {
          const chats = Array.isArray(response.data) ? response.data : [];

          if (chats.length === 0) {
            hasMorePages = false;
            break;
          }

          // Convert chats to channels format
          const channels: IChannel[] = chats.map((chat) => ({
            ...chat,
            id: chat.id,
            name: chat.id,
            description: chat.title || "Chat",
            pinned: (chat as any).isPinned || (chat as any).is_pinned || false,
            label: chat.title || "",
            metadata: chat.metadata || {},
            tags: chat.tags || [],
            agent_name: (chat as any).agent_name,
          }));

          allChannels.push(...channels);

          // Backend page size is 500, so if we got fewer than 500, we're done
          if (chats.length < 500) {
            hasMorePages = false;
          } else {
            currentPage++;
          }
        } else {
          hasMorePages = false;
          break;
        }
      }

      return {
        channels: allChannels,
        hasNextPage: false,
        totalCount: allChannels.length,
      };
    } catch (error) {
      console.error("[WS] Failed to list channels:", error);
      return {
        channels: [],
        hasNextPage: false,
        totalCount: 0,
      };
    }
  }

  // ========== Event Emitter ==========

  public on(event: string, callback: EventCallback) {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event].push(callback);
  }

  public off(event: string, callback: EventCallback) {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter((l) => l !== callback);
  }

  public emit(event: string, data: any) {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach((listener) => {
      try {
        listener(data);
      } catch (error) {
        console.error("[WS] Error in event listener:", event, error);
      }
    });
  }

  // ========== Private Methods ==========

  /**
   * Get user WebSocket URL
   */
  private getUserWebSocketUrl(): string {
    const apiBaseUrl = import.meta.env.REACT_APP_API_BASE_URL;
    const isDev = import.meta.env.DEV;
    const wsPath = "/ws/user/";

    // Build host string that includes port when set (e.g. localhost:3000)
    const hostWithPort = (hostname: string, port: string) =>
      port ? `${hostname}:${port}` : hostname;

    // In dev, use same-origin so the Vite proxy is used (must include port)
    if (isDev && typeof window !== "undefined") {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const host = hostWithPort(window.location.hostname, window.location.port);
      return `${protocol}//${host}${wsPath}`;
    }

    if (apiBaseUrl) {
      try {
        const apiUrl = new URL(apiBaseUrl);
        const wsProtocol = apiUrl.protocol === "https:" ? "wss:" : "ws:";
        return `${wsProtocol}//${apiUrl.host}${wsPath}`;
      } catch {
        // fall through to same-origin
      }
    }

    // Fallback: same-origin with port
    if (typeof window !== "undefined") {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const host = hostWithPort(window.location.hostname, window.location.port);
      return `${protocol}//${host}${wsPath}`;
    }
    return "";
  }

  /**
   * Handle incoming WebSocket message
   * Routes messages to appropriate handlers based on chat_id
   */
  private handleMessage(event: MessageEvent) {
    try {
      // Ignore messages from a stale socket (e.g. after npm reset / HMR: old socket still open, new one created)
      const currentSocket = getGlobalWsRef().socket ?? this.ws;
      if (currentSocket && event.target !== currentSocket) {
        return;
      }

      const message = JSON.parse(event.data);

      // Update heartbeat timestamp
      this.lastHeartbeat = Date.now();
      if (message.chat_id) {
        wsManager.updateHeartbeat(message.chat_id);
      }

      // Reply to our own heartbeat ack. Swallowed because it carries no
      // chat_id, which would log "without chat_id" once per heartbeat.
      if (message.type === WEBSOCKET_MESSAGE_TYPES.pong) {
        return;
      }

      // Handle heartbeat from backend (lastHeartbeat already updated above)
      if (
        message.type === WEBSOCKET_MESSAGE_TYPES.heartbeat ||
        message.type === WEBSOCKET_MESSAGE_TYPES.welcome
      ) {
        if (message.type === WEBSOCKET_MESSAGE_TYPES.heartbeat) {
          this.sendHeartbeatAck();
        }
        return;
      }

      // Handle token refresh request from backend
      if (message.type === WEBSOCKET_MESSAGE_TYPES.token_refresh_required) {
        tokenRefreshService.refresh();
        return;
      }

      // Config flags snapshot (sent on connect and whenever a flag changes)
      if (message.type === WEBSOCKET_MESSAGE_TYPES.config_flags) {
        this.configFlags = message.flags ?? {};
        window.dispatchEvent(
          new CustomEvent(WS_EVENTS.CONFIG_FLAGS_UPDATE, {
            detail: this.configFlags,
          }),
        );
        return;
      }

      // A release note was published (global broadcast, no chat_id)
      if (message.type === WEBSOCKET_MESSAGE_TYPES.release_note_published) {
        window.dispatchEvent(
          new CustomEvent(WS_EVENTS.RELEASE_NOTE_PUBLISHED, {
            detail: message.note,
          }),
        );
        return;
      }

      if (message.type === WEBSOCKET_MESSAGE_TYPES.ticket_changed) {
        window.dispatchEvent(
          new CustomEvent(WS_EVENTS.TICKET_CHANGED, { detail: message }),
        );
        return;
      }

      // A chat created for this user outside this tab — the v2 API creating one
      // on their behalf. Without this it is absent from the sidebar until a
      // reload, so it can never become the active chat and none of its turns
      // render, however correctly they are routed. Mirrors the sidebar wiring
      // createChatAndConnect/cloneChat already use; ChannelsProvider dedupes by id.
      if (
        message.type === WEBSOCKET_MESSAGE_TYPES.chat_created &&
        message.chat
      ) {
        this.emit(WS_EVENTS.CHANNEL_ADD, toChannel(message.chat));
        return;
      }

      // Extract chat_id for routing (CRITICAL - must be present)
      const chatId = message.chat_id;

      if (!chatId) {
        console.error("[WS] Received message without chat_id:", message);
        return;
      }

      if (isChatClosedWsMessage(message)) {
        this.emit(WS_EVENTS.CHAT_CLOSED, {
          chatId,
          closedAt: closedAtFromWsMessage(message) ?? null,
        });
        if (message.type === WEBSOCKET_MESSAGE_TYPES.chat_closed) {
          return;
        }
      }

      // Wake anything waiting for this write to land (sendAndAwaitAck). Falls
      // through deliberately: ChatStore still needs the frame to swap the
      // optimistic local id for the server one.
      if (
        message.type === WEBSOCKET_MESSAGE_TYPES.message_ack &&
        message.local_id
      ) {
        this.pendingAcks.get(message.local_id)?.();
      }

      // Dispatch custom event so ChatStore (or others) can process without coupling
      window.dispatchEvent(
        new CustomEvent(WS_EVENTS.MESSAGE_RECEIVED, { detail: message }),
      );

      // Emit notification for unread indicators
      // Only show notification if this is NOT the active chat
      if (
        chatId !== this.activeChat &&
        ![
          WEBSOCKET_MESSAGE_TYPES.status_message,
          WEBSOCKET_MESSAGE_TYPES.task_in_progress,
          WEBSOCKET_MESSAGE_TYPES.message_ack,
        ].includes(message.type)
      ) {
        this.emit(WS_EVENTS.CHAT_NOTIFICATION, { chatId, hasNewMessage: true });
      }

      // Handle title update (backend sends title with message when generated)
      if (
        message.title &&
        [
          WEBSOCKET_MESSAGE_TYPES.partial,
          WEBSOCKET_MESSAGE_TYPES.stream,
          WEBSOCKET_MESSAGE_TYPES.final,
          WEBSOCKET_MESSAGE_TYPES.complete,
          WEBSOCKET_MESSAGE_TYPES.agent_result,
        ].includes(message.type)
      ) {
        this.emit(WS_EVENTS.CHAT_TITLE_UPDATE, {
          chatId,
          title: message.title,
        });
      }
    } catch (error) {
      console.error("[WS] Failed to parse message:", error);
    }
  }

  /**
   * Handle WebSocket disconnect
   * Note: Token refresh service is managed by AuthContext, not stopped here
   */
  private handleDisconnect(event: CloseEvent) {
    this.isConnected = false;
    this.ws = null;
    const global = getGlobalWsRef();
    global.socket = null;
    global.connectionPromise = null;

    // Check if auth failure — stop all reconnection and redirect
    if (event.code === 4401 || event.code === 4403) {
      this.isIntentionallyClosing = true;
      this.isReconnecting = false;
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      console.error("[WS] Authentication failed, code:", event.code);
      this.emit("auth:failure", { code: event.code, reason: event.reason });
      networkEventManager.emitAuthFailure("WebSocket authentication failed");
      redirectToLogin();
      return;
    }

    // If intentional close, don't reconnect
    if (this.isIntentionallyClosing) {
      return;
    }

    const alreadyRetrying = this.isReconnecting;

    // Show reconnection popup on the first unexpected disconnect only.
    if (!alreadyRetrying) {
      networkEventManager.emitNetworkOffline("websocket_closed");
      this.attemptReconnect();
    }
    // If already retrying, let the existing attemptReconnect chain handle it.
  }

  /**
   * Attempt to reconnect with exponential backoff.
   * Never gives up — keeps retrying with a capped delay so the UI
   * recovers automatically once the server is back.
   *
   * `isReconnecting` stays true for the entire lifecycle (timer wait +
   * in-flight connectUser) so that handleOnline can detect an active
   * chain and avoid spawning a parallel one.
   */
  private attemptReconnect() {
    if (this.isIntentionallyClosing) return;

    this.isReconnecting = true;
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay,
    );

    const previousActiveChat = this.activeChat;

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      if (this.isIntentionallyClosing) {
        this.isReconnecting = false;
        return;
      }

      const connected = await this.connectUser();

      if (connected) {
        this.reconnectAttempts = 0;
        this.isReconnecting = false;
        networkEventManager.emitConnectionRecovered();

        if (previousActiveChat) {
          this.setActiveChat(previousActiveChat);
        }
      } else if (!this.isIntentionallyClosing) {
        this.attemptReconnect();
      } else {
        this.isReconnecting = false;
      }
    }, delay);
  }

  /**
   * Notify backend that token was refreshed (ping succeeded, new cookies set).
   */
  private notifyTokenRefreshed() {
    if (this.ws && this.isConnected) {
      this.ws.send(JSON.stringify({ type: "token_refreshed" }));
    }
  }

  /**
   * Answer a backend heartbeat so it knows a browser is still here (REV-2280).
   *
   * Without this the backend refreshes presence from its own send() succeeding,
   * which only proves the OS took the bytes — so a slept or disconnected tab
   * read online for ~10 minutes. `user_online` and `user_last_seen_at` on the
   * v2 API are built on it, and the backend keeps no fallback for a build that
   * never acks, so this reply is the only thing holding presence true.
   *
   * Sent as `ping` because the backend already dispatches on that type. Silence
   * is the signal, so anything that stops onmessage stops presence: a tab the
   * browser froze or suspended reads offline while open (backgrounded on
   * mobile, tab freezing or Memory Saver on desktop), which is why the
   * constructor re-acks on `visibilitychange` rather than waiting a heartbeat.
   */
  private sendHeartbeatAck() {
    if (!this.ws || !this.isConnected) {
      return;
    }
    try {
      this.ws.send(JSON.stringify({ type: "ping" }));
    } catch {
      // Socket already closing — onclose handles it.
    }
  }

  /**
   * Handle online event — browser regained network.
   * Cancel any pending timer and reset the attempt counter so the next
   * retry uses fast backoff. If connectUser() is already in-flight, let
   * it finish — its follow-up will use the reset counter. Otherwise start
   * a fresh chain.
   */
  private handleOnline() {
    this.isOnline = true;
    networkEventManager.emitNetworkOnline();

    if (!this.isConnected) {
      this.reconnectAttempts = 0;

      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
        // Timer was the active work — clear the flag so we can start fresh
        this.isReconnecting = false;
      }

      if (!this.isReconnecting) {
        this.attemptReconnect();
      }
      // If isReconnecting is still true here, connectUser() is in-flight.
      // Its follow-up will use the reset reconnectAttempts counter.
    }
  }

  /**
   * Handle offline event
   */
  private handleOffline() {
    this.isOnline = false;
    networkEventManager.emitNetworkOffline("browser_offline");
  }
}

// Single instance per tab: reuse from window so duplicate module loads (e.g. chunks) don't create extra connections
const GLOBAL_SERVICE_KEY = "__app_websocket_service";
function getSingleton(): WebSocketService {
  if (typeof window === "undefined") return new WebSocketService();
  const w = window as any;
  if (!w[GLOBAL_SERVICE_KEY]) w[GLOBAL_SERVICE_KEY] = new WebSocketService();
  return w[GLOBAL_SERVICE_KEY];
}
export const websocketService = getSingleton();

// Expose for debugging in development
if (typeof window !== "undefined" && import.meta.env.MODE === "development") {
  (window as any).websocketService = websocketService;
}
